import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  Eye,
  EyeOff,
  FileText,
  Layers,
  Lock,
  LogIn,
  Sparkles,
  User,
  UserPlus,
} from 'lucide-react';
import { useTutorialAuth } from '@/context/TutorialAuthContext';
import { IPE_MANUAL_HOME_PATH } from '@/config/ipeRoutes';
import '@/styles/tutorial/tutorial-manual.css';

export default function TutorialManualLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, isAuthenticated } = useTutorialAuth();
  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = mode === 'register' ? '教程注册 - IPE 使用手册' : '教程登录 - IPE 使用手册';
  }, [mode]);

  useEffect(() => {
    if (isAuthenticated) {
      const redirect = searchParams.get('redirect') || IPE_MANUAL_HOME_PATH;
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (mode === 'register' && password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'register') {
        await register(username.trim(), password);
      } else {
        await login(username.trim(), password);
      }
      const redirect = searchParams.get('redirect') || IPE_MANUAL_HOME_PATH;
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(err.message || (mode === 'register' ? '注册失败' : '登录失败'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tutorial-login">
      <aside className="tutorial-login__hero" aria-hidden="true">
        <div className="tutorial-login__hero-bg" />
        <div className="tutorial-login__hero-content">
          <div className="tutorial-login__hero-badge">
            <Sparkles size={14} />
            <span>Product Guide</span>
          </div>
          <h2 className="tutorial-login__hero-title">IPE 使用手册</h2>
          <p className="tutorial-login__hero-desc">
            系统化产品教程与操作指引，助你快速上手 IPE 工作台各项能力。
          </p>
          <ul className="tutorial-login__hero-features">
            <li>
              <span className="tutorial-login__hero-feature-icon">
                <BookOpen size={16} />
              </span>
              <span>分章节浏览，按需查阅</span>
            </li>
            <li>
              <span className="tutorial-login__hero-feature-icon">
                <FileText size={16} />
              </span>
              <span>注册即可阅读完整教程</span>
            </li>
            <li>
              <span className="tutorial-login__hero-feature-icon">
                <Layers size={16} />
              </span>
              <span>编辑权限可申请或授权</span>
            </li>
          </ul>
        </div>
      </aside>

      <main className="tutorial-login__main">
        <Link to={IPE_MANUAL_HOME_PATH} className="tutorial-login__back">
          <ArrowLeft size={16} />
          返回手册首页
        </Link>

        <div className="tutorial-login__panel">
          <div className="tutorial-login__brand">
            <div className="tutorial-login__brand-icon">
              <BookOpen size={22} />
            </div>
            <div>
              <h1>{mode === 'register' ? '创建教程账号' : '欢迎回来'}</h1>
              <p>
                {mode === 'register'
                  ? '注册后即可查阅教程；编辑权限需管理员授权或申请审核。'
                  : '登录你的教程账号，继续阅读与编辑。'}
              </p>
            </div>
          </div>

          <div className="tutorial-login__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'login'}
              className={mode === 'login' ? 'tutorial-login__tab tutorial-login__tab--active' : 'tutorial-login__tab'}
              onClick={() => switchMode('login')}
            >
              登录
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'register'}
              className={mode === 'register' ? 'tutorial-login__tab tutorial-login__tab--active' : 'tutorial-login__tab'}
              onClick={() => switchMode('register')}
            >
              注册
            </button>
          </div>

          <form className="tutorial-login__form" onSubmit={handleSubmit}>
            <label className="tutorial-login__field">
              <span className="tutorial-login__field-label">用户名</span>
              <span className="tutorial-login__input-wrap">
                <User size={16} className="tutorial-login__input-icon" />
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={mode === 'register' ? '设置登录用户名' : '请输入教程账号'}
                  autoComplete="username"
                  required
                />
              </span>
            </label>
            <label className="tutorial-login__field">
              <span className="tutorial-login__field-label">密码</span>
              <span className="tutorial-login__input-wrap tutorial-login__input-wrap--password">
                <Lock size={16} className="tutorial-login__input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? '至少 6 位' : '请输入密码'}
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="tutorial-login__password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? '隐藏密码' : '显示密码'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </span>
            </label>
            {mode === 'register' ? (
              <label className="tutorial-login__field">
                <span className="tutorial-login__field-label">确认密码</span>
                <span className="tutorial-login__input-wrap tutorial-login__input-wrap--password">
                  <Lock size={16} className="tutorial-login__input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="再次输入密码"
                    autoComplete="new-password"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="tutorial-login__password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? '隐藏密码' : '显示密码'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </span>
              </label>
            ) : null}
            {error ? <p className="tutorial-login__error">{error}</p> : null}
            <button type="submit" className="tutorial-login__submit" disabled={submitting}>
              {mode === 'register' ? <UserPlus size={16} /> : <LogIn size={16} />}
              {submitting ? (mode === 'register' ? '注册中…' : '登录中…') : (mode === 'register' ? '注册账号' : '登录')}
            </button>
          </form>

          <p className="tutorial-login__hint">无需登录也可阅读公开教程</p>
        </div>
      </main>
    </div>
  );
}
